import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Card, CardBody, CardText, CardTitle, Col, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { toast } from "react-toastify";
import { downloadFile, getFile } from "../../../slices/thunks";
import OPlaceholder from "../../../Components/OElements/OPlaceholder";

const PersonalProtectiveEquipmentFilesModal = ({ dataPersonalProtectiveEquipment, isOpen, handleToggleModal }) => {
    const dispatch = useDispatch();

    const [documentIds, setDocumentIds] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [fileDownloadLoading, setFileDownloadLoading] = useState(null);
    const [loading, setLoading] = useState(false);

    // Update document IDs whenever dataPersonalProtectiveEquipment changes
    useEffect(() => {
        if (dataPersonalProtectiveEquipment) {
            const listDocuments = [];

            if (dataPersonalProtectiveEquipment.document_id) {
                listDocuments.push(dataPersonalProtectiveEquipment.document_id);
            }

            if (dataPersonalProtectiveEquipment.receive_document_id) {
                listDocuments.push(dataPersonalProtectiveEquipment.receive_document_id);
            }

            if (dataPersonalProtectiveEquipment.pick_document_id) {
                listDocuments.push(dataPersonalProtectiveEquipment.pick_document_id);
            }

            setDocumentIds(listDocuments);
        }
    }, [dataPersonalProtectiveEquipment]);

    // Fetch documents asynchronously
    useEffect(() => {
        const fetchFiles = async () => {
            // Elimina IDs duplicados
            const uniqueDocumentIds = [...new Set(documentIds)];

            if (uniqueDocumentIds.length > 0) {
                setLoading(true);
                try {
                    const files = await Promise.all(
                        uniqueDocumentIds.map(async (documentId) => {
                            const response = await dispatch(getFile(documentId));
                            return { id: documentId, data: response.payload };
                        })
                    );
                    setDocuments(files);
                } catch (error) {
                    console.error("Error fetching files: ", error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchFiles();
    }, [documentIds, dispatch]);


    const handleDownloadFile = (file) => {
        setFileDownloadLoading(file._id);
        dispatch(downloadFile({ file_id: file._id }))
            .then(data => {
                const payload = data.payload;
                const url = window.URL.createObjectURL(payload);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${file.filename}${file.extension}`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .finally(() => setFileDownloadLoading(null));
    };


    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const togglePreviewModal = () => setIsPreviewModalOpen(!isPreviewModalOpen);
    const [previewFile, setPreviewFile] = useState(null);


    const handlePreviewClick = async (file) => {
        try {
            const result = await dispatch(downloadFile({ file_id: file._id, disable_msg: true }));
            const blob = result.payload;

            if (!(blob instanceof Blob)) {
                throw new Error("El archivo no es un Blob válido.");
            }

            const fileURL = URL.createObjectURL(blob);
            setPreviewFile({ ...file, url: fileURL });
            togglePreviewModal();
        } catch (error) {
            console.error('Error al obtener el archivo:', error);
        }
    };



    return (
        <div>
            <Modal
                isOpen={isOpen}
                toggle={handleToggleModal}
                size="lg"
                className="border-0"
                modalClassName="modal fade zoomIn"
                backdrop="static"
                centered={true}
            >
                <ModalHeader toggle={handleToggleModal}>Documentos</ModalHeader>
                <ModalBody>
                    <Row>
                        {loading ? (
                            <OPlaceholder />
                        ) : documents.length > 0 ? (
                            documents.map((item, index) => (
                                <Col key={`${item.id}_${index}`} md={6}>
                                    <Card className="border">
                                        <CardBody>
                                            <Row>
                                                <Col md={2}>
                                                    <i className={`${Icons.guide} ri-3x`}></i>
                                                </Col>
                                                <Col>
                                                    <CardTitle tag="h5" className="mt-2">
                                                        {item.data?.file_type?.name || 'Documento'}
                                                    </CardTitle>
                                                    <CardText>{item.data?.filename}</CardText>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                        <CardBody>
                                            <div className='d-flex align-items-end justify-content-end'>
                                                <div className='flex-shrink-0'>
                                                    <div className='d-flex flex-wrap gap-2'>

                                                        <button
                                                            className='btn btn-label btn-primary'
                                                            onClick={() => handleDownloadFile(item.data)}
                                                        >
                                                            <i className='icon-download label-icon'></i>{' '}
                                                            Descargar
                                                        </button>

                                                        <button
                                                            className='btn btn-label btn-primary'
                                                            onClick={() => handlePreviewClick(item.data)}
                                                        >
                                                            <i className='ri-eye-line label-icon'></i>{' '}
                                                            Previsualizar
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))
                        ) : (
                            <Col className="text-center">
                                <p>No hay documentos disponibles.</p>
                            </Col>
                        )}
                    </Row>
                </ModalBody>
            </Modal>

            <Modal
                isOpen={isPreviewModalOpen}
                toggle={togglePreviewModal}
                centered={false}
                className="w-100 p-4"
                fullscreen={true}
                scrollable={false}
            >
                <ModalHeader toggle={togglePreviewModal}>Previsualización</ModalHeader>
                <ModalBody className="overflow-hidden">
                    {previewFile && previewFile.url ? (
                        previewFile.mime_type.includes('image') ? (
                            <img src={previewFile.url} alt={previewFile.filename} className='img-fluid' />
                        ) : previewFile.mime_type.includes('pdf') ? (
                            <iframe src={previewFile.url} width='100%' height='100%' />
                        ) : previewFile.mime_type.includes('text') ? (
                            <pre>{previewFile.content || 'No se pudo cargar el contenido'}</pre>
                        ) : (
                            <p>Formato no compatible para previsualización.</p>
                        )
                    ) : (
                        <p>Cargando...</p>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
};

export default PersonalProtectiveEquipmentFilesModal;
